import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Users, Briefcase, Shield, CreditCard, MessageSquare, Settings,
  AlertTriangle, Clock, CheckCircle, XCircle, Search, MapPin,
  LogOut, RefreshCw, Eye, Ban, FileText, Map, Navigation,
  DollarSign, Activity, BarChart3, Edit, RotateCcw, UserPlus
} from 'lucide-react';
import AdminPricingSettings from '../components/AdminPricingSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available roles for role change
const ALL_ROLES = [
  { value: 'customer', label: 'Customer' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'support_agent', label: 'Support Agent' },
  { value: 'ops_dispatch', label: 'Ops Dispatch' },
  { value: 'verification_reviewer', label: 'Verification Reviewer' },
  { value: 'finance', label: 'Finance' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [adminAccess, setAdminAccess] = useState(null);
  const [error, setError] = useState(null);
  
  // Dashboard stats
  const [stats, setStats] = useState(null);
  const [liveJobs, setLiveJobs] = useState([]);
  
  // Users
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  
  // Contractors
  const [contractors, setContractors] = useState([]);
  const [contractorPage, setContractorPage] = useState(1);
  const [contractorLocations, setContractorLocations] = useState([]);
  
  // Jobs
  const [jobs, setJobs] = useState([]);
  const [jobPage, setJobPage] = useState(1);
  const [jobFilter, setJobFilter] = useState('');
  
  // Verifications
  const [verificationQueue, setVerificationQueue] = useState({ identities: [], certifications: [] });
  
  // Payments
  const [payments, setPayments] = useState([]);
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleChangeTarget, setRoleChangeTarget] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [showJobActionsModal, setShowJobActionsModal] = useState(false);
  const [showAssignContractorModal, setShowAssignContractorModal] = useState(false);
  const [availableContractors, setAvailableContractors] = useState([]);
  const [assignmentWarnings, setAssignmentWarnings] = useState(null);

  // Get token from localStorage as fallback
  const getToken = useCallback(() => {
    return token || localStorage.getItem('token');
  }, [token]);

  // Check admin access
  useEffect(() => {
    const checkAccess = async () => {
      const authToken = getToken();
      
      if (!authToken) {
        navigate('/auth');
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/admin/access-check`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        
        const data = await res.json();
        
        if (!data.has_access) {
          toast.error('You do not have admin access');
          navigate('/');
          return;
        }
        
        setAdminAccess(data);
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };
    
    checkAccess();
  }, [navigate, getToken]);

  // Fetch functions
  const fetchStats = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [getToken]);

  const fetchLiveJobs = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/dashboard/live-jobs?limit=20`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setLiveJobs(data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch live jobs:', error);
    }
  }, [getToken]);

  const fetchUsers = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users?search=${userSearch}&page=${userPage}&limit=20`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setUsers(data.users || []);
      setUserTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [getToken, userSearch, userPage]);

  const fetchContractors = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/contractors-list?page=${contractorPage}&limit=20`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setContractors(data.contractors || []);
    } catch (error) {
      console.error('Failed to fetch contractors:', error);
    }
  }, [getToken, contractorPage]);

  const fetchContractorLocations = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/contractors/live-locations`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setContractorLocations(data.contractors || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, [getToken]);

  const fetchJobs = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const url = `${API_URL}/api/admin/jobs-list?page=${jobPage}&limit=20${jobFilter ? `&status=${jobFilter}` : ''}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setJobs(data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  }, [getToken, jobPage, jobFilter]);

  const fetchJobDetail = useCallback(async (jobId) => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/jobs-detail/${jobId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setJobDetail(data);
    } catch (error) {
      console.error('Failed to fetch job detail:', error);
    }
  }, [getToken]);

  const fetchVerifications = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/verifications/queue`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setVerificationQueue(data);
    } catch (error) {
      console.error('Failed to fetch verifications:', error);
    }
  }, [getToken]);

  const fetchPayments = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/payments-list?limit=50`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setPayments(data.payments || []);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    }
  }, [getToken]);

  const fetchAuditLogs = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/audit-logs?limit=100`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setAuditLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  }, [getToken]);

  // Load data based on active tab
  useEffect(() => {
    if (!adminAccess) return;
    
    if (activeTab === 'dashboard') {
      fetchStats();
      fetchLiveJobs();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'contractors') {
      fetchContractors();
    } else if (activeTab === 'jobs') {
      fetchJobs();
    } else if (activeTab === 'verifications') {
      fetchVerifications();
    } else if (activeTab === 'payments') {
      fetchPayments();
    } else if (activeTab === 'audit') {
      fetchAuditLogs();
    } else if (activeTab === 'map') {
      fetchContractorLocations();
    }
  }, [activeTab, adminAccess, fetchStats, fetchLiveJobs, fetchUsers, fetchContractors, fetchJobs, fetchVerifications, fetchPayments, fetchAuditLogs, fetchContractorLocations]);

  // Auto-refresh
  useEffect(() => {
    if (activeTab === 'dashboard') {
      const interval = setInterval(() => {
        fetchStats();
        fetchLiveJobs();
      }, 30000);
      return () => clearInterval(interval);
    }
    if (activeTab === 'map') {
      const interval = setInterval(fetchContractorLocations, 15000);
      return () => clearInterval(interval);
    }
  }, [activeTab, fetchStats, fetchLiveJobs, fetchContractorLocations]);

  // ========== ACTION HANDLERS ==========
  
  // Change user role (SUPER_ADMIN only)
  const handleRoleChange = async () => {
    if (!roleChangeTarget || !newRole) return;
    
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${roleChangeTarget.id}/change-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ new_role: newRole })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Role changed: ${data.old_role} → ${data.new_role}`);
        setShowRoleModal(false);
        setRoleChangeTarget(null);
        setNewRole('');
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to change role');
      }
    } catch (error) {
      toast.error('Error changing role');
    }
  };

  const handleSuspendUser = async (userId, reason) => {
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ reason })
      });
      
      if (res.ok) {
        toast.success('User suspended');
        fetchUsers();
      } else {
        toast.error('Failed to suspend user');
      }
    } catch (error) {
      toast.error('Error suspending user');
    }
  };

  const handleUnsuspendUser = async (userId) => {
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/unsuspend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        toast.success('User unsuspended');
        fetchUsers();
      }
    } catch (error) {
      toast.error('Error unsuspending user');
    }
  };

  const handleCancelJob = async (jobId, reason, refund = true) => {
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/jobs/${jobId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ reason, refund })
      });
      
      if (res.ok) {
        toast.success('Job cancelled');
        setJobDetail(null);
        setSelectedJob(null);
        fetchJobs();
      } else {
        toast.error('Failed to cancel job');
      }
    } catch (error) {
      toast.error('Error cancelling job');
    }
  };

  const handleRedispatch = async (jobId, reason) => {
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/jobs/${jobId}/redispatch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ reason })
      });
      
      if (res.ok) {
        toast.success('Job re-dispatched');
        fetchJobDetail(jobId);
      } else {
        toast.error('Failed to re-dispatch');
      }
    } catch (error) {
      toast.error('Error re-dispatching');
    }
  };

  const searchContractorsForAssignment = async (jobId) => {
    const authToken = getToken();
    try {
      // Get job details to get location
      const jobRes = await fetch(`${API_URL}/api/admin/jobs-detail/${jobId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const jobData = await jobRes.json();
      
      let url = `${API_URL}/api/admin/contractors/search?limit=20`;
      if (jobData.address?.lat && jobData.address?.lng) {
        url += `&lat=${jobData.address.lat}&lng=${jobData.address.lng}`;
      }
      if (jobData.job?.primary_trade_id) {
        url += `&trade_id=${jobData.job.primary_trade_id}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setAvailableContractors(data.contractors || []);
      setShowAssignContractorModal(true);
    } catch (error) {
      toast.error('Error searching contractors');
    }
  };

  const handleForceAssign = async (jobId, contractorId, override = false) => {
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/jobs/${jobId}/force-assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ 
          contractor_id: contractorId,
          reason: 'Admin assignment',
          override_checks: override
        })
      });
      
      const data = await res.json();
      
      if (data.requires_override) {
        setAssignmentWarnings(data);
        return;
      }
      
      if (res.ok && data.success) {
        toast.success(`Assigned to ${data.contractor_name}`);
        setShowAssignContractorModal(false);
        setAssignmentWarnings(null);
        fetchJobDetail(jobId);
      } else {
        toast.error(data.detail || 'Assignment failed');
      }
    } catch (error) {
      toast.error('Error assigning contractor');
    }
  };

  const handleRefund = async (paymentId, reason) => {
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/payments/${paymentId}/refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ reason })
      });
      
      if (res.ok) {
        toast.success('Refund processed');
        fetchPayments();
      }
    } catch (error) {
      toast.error('Error processing refund');
    }
  };

  const handleSendMessage = async (userId, content) => {
    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/support/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ user_id: userId, content })
      });
      
      if (res.ok) {
        toast.success('Message sent as ZOLT Support');
      }
    } catch (error) {
      toast.error('Error sending message');
    }
  };

  // Loading & Error states
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mx-auto mb-4"></div>
          <div className="text-white text-xl">Loading Admin Panel...</div>
        </div>
      </div>
    );
  }

  if (error || !adminAccess) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠</div>
          <div className="text-white text-xl mb-2">Access Error</div>
          <div className="text-neutral-400 text-sm mb-4">{error || 'No admin access'}</div>
          <Button onClick={() => window.location.reload()} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'contractors', label: 'Contractors', icon: Briefcase },
    { id: 'jobs', label: 'Jobs', icon: FileText },
    { id: 'map', label: 'Live Map', icon: Map },
    { id: 'verifications', label: 'Verifications', icon: Shield },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'pricing', label: 'Pricing v2', icon: DollarSign },
    { id: 'audit', label: 'Audit Logs', icon: Activity },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const getStatusColor = (status) => {
    const colors = {
      searching: 'bg-yellow-500',
      accepted: 'bg-blue-500',
      en_route: 'bg-purple-500',
      arrived: 'bg-indigo-500',
      in_progress: 'bg-orange-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500',
      expired: 'bg-neutral-500'
    };
    return colors[status] || 'bg-neutral-500';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-red-500/20 text-red-400 border-red-500/50',
      admin: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      support_agent: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      ops_dispatch: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
      verification_reviewer: 'bg-green-500/20 text-green-400 border-green-500/50',
      finance: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      contractor: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      customer: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/50',
    };
    return colors[role] || colors.customer;
  };

  const isSuperAdmin = adminAccess?.role === 'super_admin';

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex" data-testid="admin-panel">
      {/* Sidebar */}
      <aside className="w-64 bg-neutral-900 border-r border-neutral-800 flex flex-col">
        <div className="p-6 border-b border-neutral-800">
          <h1 className="text-2xl font-bold">ZOLT Admin</h1>
          <p className="text-sm text-neutral-400 mt-1">Operations Centre</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                activeTab === tab.id
                  ? 'bg-white text-black'
                  : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </nav>
        
        <div className="p-4 border-t border-neutral-800">
          <div className="text-sm text-neutral-400 mb-2">Logged in as</div>
          <div className="font-medium">{adminAccess?.name}</div>
          <div className={`text-xs px-2 py-1 rounded border mt-1 inline-block ${getRoleBadgeColor(adminAccess?.role)}`}>
            {adminAccess?.role}
          </div>
          <Button
            onClick={() => { logout(); navigate('/'); }}
            variant="outline"
            className="w-full mt-4 border-neutral-700"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* ========== DASHBOARD TAB ========== */}
        {activeTab === 'dashboard' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Operations Dashboard</h2>
              <Button onClick={() => { fetchStats(); fetchLiveJobs(); }} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {/* KPI Cards */}
            {stats && (
              <div className="grid grid-cols-4 gap-4 mb-8">
                <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-400">Jobs Today</span>
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-3xl font-bold">{stats.jobs_today}</div>
                  <div className="text-sm text-neutral-500 mt-1">
                    {stats.jobs_active} active • {stats.jobs_searching} searching
                  </div>
                </div>

                <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-400">GMV Today</span>
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="text-3xl font-bold">£{stats.gmv_today?.toLocaleString()}</div>
                  <div className="text-sm text-green-400 mt-1">
                    Platform: £{stats.platform_revenue_today?.toLocaleString()} (6%)
                  </div>
                </div>

                <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-400">Online Contractors</span>
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="text-3xl font-bold">{stats.online_contractors}</div>
                  <div className="text-sm text-neutral-500 mt-1">
                    of {stats.total_contractors} total
                  </div>
                </div>

                <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-400">Pending Actions</span>
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="text-3xl font-bold">
                    {(stats.pending_verifications || 0) + (stats.pending_certifications || 0)}
                  </div>
                  <div className="text-sm text-neutral-500 mt-1">
                    {stats.open_tickets || 0} open tickets
                  </div>
                </div>
              </div>
            )}

            {/* Live Jobs */}
            <div className="bg-neutral-900 rounded-xl border border-neutral-800">
              <div className="p-4 border-b border-neutral-800 flex items-center justify-between">
                <h3 className="font-semibold">Live Jobs</h3>
                <span className="text-sm text-neutral-400">{liveJobs.length} active</span>
              </div>
              <div className="divide-y divide-neutral-800 max-h-96 overflow-auto">
                {liveJobs.length === 0 ? (
                  <div className="p-8 text-center text-neutral-500">No active jobs</div>
                ) : (
                  liveJobs.map(job => (
                    <div key={job.id} className="p-4 hover:bg-neutral-800/50 flex items-center gap-4 cursor-pointer"
                         onClick={() => { setSelectedJob(job); fetchJobDetail(job.id); }}>
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(job.status)}`} />
                      <div className="flex-1">
                        <div className="font-medium">{job.category} - {job.subcategory}</div>
                        <div className="text-sm text-neutral-400">
                          {job.customer?.name || 'Unknown'} • £{job.estimated_price}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(job.status)} bg-opacity-20`}>
                        {job.status?.replace('_', ' ')}
                      </span>
                      <Eye className="w-4 h-4 text-neutral-400" />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========== USERS TAB ========== */}
        {activeTab === 'users' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">User Management</h2>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-9 bg-neutral-900 border-neutral-700 w-64"
                  />
                </div>
                <Button onClick={fetchUsers} variant="outline">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-800 text-left">
                    <th className="p-4 text-neutral-400 font-medium">Name</th>
                    <th className="p-4 text-neutral-400 font-medium">Email</th>
                    <th className="p-4 text-neutral-400 font-medium">Role</th>
                    <th className="p-4 text-neutral-400 font-medium">Status</th>
                    <th className="p-4 text-neutral-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-neutral-800 hover:bg-neutral-800/50">
                      <td className="p-4">{u.name}</td>
                      <td className="p-4 text-neutral-400">{u.email}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs border ${getRoleBadgeColor(u.role)}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="p-4">
                        {u.is_suspended ? (
                          <span className="text-red-400">Suspended</span>
                        ) : (
                          <span className="text-green-400">Active</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedUser(u)} title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {/* Role Change Button (SUPER_ADMIN only) */}
                          {isSuperAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setRoleChangeTarget(u);
                                setNewRole(u.role);
                                setShowRoleModal(true);
                              }}
                              title="Change Role"
                            >
                              <Edit className="w-4 h-4 text-blue-400" />
                            </Button>
                          )}
                          
                          {!u.is_suspended && u.role !== 'super_admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const reason = prompt('Suspension reason:');
                                if (reason) handleSuspendUser(u.id, reason);
                              }}
                              title="Suspend User"
                            >
                              <Ban className="w-4 h-4 text-red-400" />
                            </Button>
                          )}
                          
                          {u.is_suspended && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnsuspendUser(u.id)}
                              title="Unsuspend User"
                            >
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const msg = prompt('Send message as ZOLT Support:');
                              if (msg) handleSendMessage(u.id, msg);
                            }}
                            title="Message User"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-4 flex items-center justify-between border-t border-neutral-800">
                <span className="text-sm text-neutral-400">Total: {userTotal} users</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={userPage === 1} onClick={() => setUserPage(p => p - 1)}>
                    Previous
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setUserPage(p => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== CONTRACTORS TAB ========== */}
        {activeTab === 'contractors' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Contractor Management</h2>
            <div className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-800 text-left">
                    <th className="p-4 text-neutral-400 font-medium">Contractor</th>
                    <th className="p-4 text-neutral-400 font-medium">Company</th>
                    <th className="p-4 text-neutral-400 font-medium">Status</th>
                    <th className="p-4 text-neutral-400 font-medium">Online</th>
                    <th className="p-4 text-neutral-400 font-medium">Stats</th>
                    <th className="p-4 text-neutral-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contractors.map(c => (
                    <tr key={c.user_id} className="border-b border-neutral-800 hover:bg-neutral-800/50">
                      <td className="p-4">
                        <div>{c.user?.name || 'Unknown'}</div>
                        <div className="text-sm text-neutral-400">{c.user?.email}</div>
                      </td>
                      <td className="p-4">{c.company_name || '-'}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          c.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                          c.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className={`w-3 h-3 rounded-full ${c.is_online ? 'bg-green-500' : 'bg-neutral-600'}`} />
                      </td>
                      <td className="p-4 text-sm">
                        <div>{c.stats?.jobs_completed || 0} jobs</div>
                        <div className="text-yellow-400">★ {c.stats?.rating?.toFixed(1) || '5.0'}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {c.is_online && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const reason = prompt('Reason for forcing offline:');
                                if (reason) {
                                  const authToken = getToken();
                                  fetch(`${API_URL}/api/admin/contractors/${c.user_id}/force-offline`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                                    body: JSON.stringify({ reason })
                                  }).then(() => { toast.success('Forced offline'); fetchContractors(); });
                                }
                              }}
                            >
                              <XCircle className="w-4 h-4 text-red-400" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========== JOBS TAB ========== */}
        {activeTab === 'jobs' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Job Management</h2>
              <div className="flex gap-2">
                <select
                  value={jobFilter}
                  onChange={(e) => setJobFilter(e.target.value)}
                  className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2"
                >
                  <option value="">All Statuses</option>
                  <option value="searching">Searching</option>
                  <option value="accepted">Accepted</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <Button onClick={fetchJobs} variant="outline">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-800 text-left">
                    <th className="p-4 text-neutral-400 font-medium">Job</th>
                    <th className="p-4 text-neutral-400 font-medium">Customer</th>
                    <th className="p-4 text-neutral-400 font-medium">Contractor</th>
                    <th className="p-4 text-neutral-400 font-medium">Price</th>
                    <th className="p-4 text-neutral-400 font-medium">Status</th>
                    <th className="p-4 text-neutral-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <tr key={job.id} className="border-b border-neutral-800 hover:bg-neutral-800/50">
                      <td className="p-4">
                        <div>{job.category}</div>
                        <div className="text-sm text-neutral-400">{job.subcategory}</div>
                      </td>
                      <td className="p-4">
                        <div>{job.customer?.name || 'Unknown'}</div>
                        <div className="text-sm text-neutral-400">{job.customer?.email}</div>
                      </td>
                      <td className="p-4">
                        {job.contractor_user ? (
                          <div>{job.contractor_user.name}</div>
                        ) : (
                          <span className="text-neutral-500">Unassigned</span>
                        )}
                      </td>
                      <td className="p-4">£{job.estimated_price?.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(job.status)} bg-opacity-20`}>
                          {job.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-4">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => { setSelectedJob(job); fetchJobDetail(job.id); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========== LIVE MAP TAB ========== */}
        {activeTab === 'map' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Live Ops Dashboard</h2>
                <p className="text-neutral-400 text-sm">Real-time contractor locations and job dispatch</p>
              </div>
              <Button onClick={fetchContractorLocations} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {/* Stats Bar */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-green-900/30 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-green-400 text-sm">Online</span>
                </div>
                <div className="text-2xl font-bold text-white">{contractorLocations.length}</div>
                <div className="text-neutral-400 text-xs">contractors</div>
              </div>
              <div className="bg-blue-900/30 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Navigation className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-400 text-sm">Available</span>
                </div>
                <div className="text-2xl font-bold text-white">
                  {contractorLocations.filter(c => c.is_available !== false && !c.active_job).length}
                </div>
                <div className="text-neutral-400 text-xs">ready for jobs</div>
              </div>
              <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm">Busy</span>
                </div>
                <div className="text-2xl font-bold text-white">
                  {contractorLocations.filter(c => c.active_job).length}
                </div>
                <div className="text-neutral-400 text-xs">on active jobs</div>
              </div>
              <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-400 text-sm">Searching</span>
                </div>
                <div className="text-2xl font-bold text-white">{liveJobs.filter(j => j.status === 'searching').length}</div>
                <div className="text-neutral-400 text-xs">jobs searching</div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-6">
              {/* Map */}
              <div className="col-span-2 bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
                <div className="p-4 border-b border-neutral-800 flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Map className="w-5 h-5 text-blue-400" />
                    Live Map
                  </h3>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="text-neutral-400">Available</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="text-neutral-400">Busy</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-purple-500" />
                      <span className="text-neutral-400">Job Location</span>
                    </div>
                  </div>
                </div>
                <div className="min-h-[400px] flex items-center justify-center bg-neutral-950 relative">
                  {/* Map integration would go here - using placeholder */}
                  <div className="text-center p-8">
                    <Map className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
                    <p className="text-neutral-400 font-medium">Google Maps Integration</p>
                    <p className="text-sm text-neutral-500 mt-2">
                      {contractorLocations.length} contractors • {liveJobs.filter(j => j.status === 'searching').length} searching jobs
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      {contractorLocations.slice(0, 5).map(c => (
                        <div key={c.contractor_id} className="bg-neutral-800 px-3 py-1 rounded-full text-xs flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${c.active_job ? 'bg-amber-500' : 'bg-green-500'}`} />
                          {c.name?.split(' ')[0]}
                        </div>
                      ))}
                      {contractorLocations.length > 5 && (
                        <div className="bg-neutral-800 px-3 py-1 rounded-full text-xs text-neutral-400">
                          +{contractorLocations.length - 5} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Contractor List */}
              <div className="bg-neutral-900 rounded-xl border border-neutral-800">
                <div className="p-4 border-b border-neutral-800">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-400" />
                    Online Contractors ({contractorLocations.length})
                  </h3>
                </div>
                <div className="divide-y divide-neutral-800 max-h-[400px] overflow-auto">
                  {contractorLocations.length === 0 ? (
                    <div className="p-8 text-center text-neutral-500">
                      <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No contractors online</p>
                    </div>
                  ) : (
                    contractorLocations.map(c => (
                      <div key={c.contractor_id} className="p-4 hover:bg-neutral-800/50 transition-colors">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${c.active_job ? 'bg-amber-500' : 'bg-green-500'} animate-pulse`} />
                            <span className="font-medium text-white">{c.name}</span>
                          </div>
                          {!c.active_job && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                              Available
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-neutral-400 flex flex-wrap gap-1 mt-1">
                          {(c.trades || [c.trade]).filter(Boolean).map(trade => (
                            <span key={trade} className="bg-neutral-800 px-2 py-0.5 rounded text-xs">{trade}</span>
                          ))}
                        </div>
                        <div className="text-xs text-neutral-500 mt-2 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {c.lat?.toFixed(4)}, {c.lng?.toFixed(4)}
                          <span className="mx-1">•</span>
                          {c.radius_miles || 15} mi radius
                        </div>
                        {c.active_job && (
                          <div className="mt-2 bg-amber-500/20 border border-amber-500/30 rounded-lg p-2">
                            <div className="text-xs text-amber-400 font-medium mb-1">Active Job</div>
                            <div className="text-xs text-neutral-300">{c.active_job.category}</div>
                            <div className="text-xs text-neutral-500">{c.active_job.status}</div>
                          </div>
                        )}
                        <div className="text-xs text-neutral-600 mt-2">
                          Last seen: {c.last_seen_at ? new Date(c.last_seen_at).toLocaleTimeString() : 'Unknown'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            
            {/* Searching Jobs Section */}
            <div className="mt-6 bg-neutral-900 rounded-xl border border-neutral-800">
              <div className="p-4 border-b border-neutral-800 flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-400" />
                  Jobs Searching for Contractors
                </h3>
                <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-sm">
                  {liveJobs.filter(j => j.status === 'searching').length} active
                </span>
              </div>
              <div className="divide-y divide-neutral-800 max-h-[300px] overflow-auto">
                {liveJobs.filter(j => j.status === 'searching').length === 0 ? (
                  <div className="p-8 text-center text-neutral-500">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No jobs currently searching</p>
                  </div>
                ) : (
                  liveJobs.filter(j => j.status === 'searching').map(job => (
                    <div key={job.id} className="p-4 flex items-center justify-between hover:bg-neutral-800/50">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-white">{job.category}</span>
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                            Searching
                          </span>
                        </div>
                        <div className="text-sm text-neutral-400 mt-1 flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {job.address_text || 'Location hidden'}
                        </div>
                        <div className="text-xs text-neutral-500 mt-1">
                          Customer: {job.customer_name || 'Unknown'} • Created: {new Date(job.created_at).toLocaleTimeString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-lg font-bold text-white">£{job.estimated_price?.toFixed(0) || job.price?.toFixed(0) || '—'}</div>
                          <div className="text-xs text-neutral-500">est. price</div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedJob(job);
                            fetchJobDetail(job.id);
                            setShowJobActionsModal(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========== VERIFICATIONS TAB ========== */}
        {activeTab === 'verifications' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Verification Centre</h2>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-neutral-900 rounded-xl border border-neutral-800">
                <div className="p-4 border-b border-neutral-800">
                  <h3 className="font-semibold">Identity Verifications</h3>
                  <span className="text-sm text-neutral-400">{verificationQueue.identities_count || 0} pending</span>
                </div>
                <div className="divide-y divide-neutral-800">
                  {verificationQueue.identities?.length === 0 ? (
                    <div className="p-8 text-center text-neutral-500">No pending verifications</div>
                  ) : (
                    verificationQueue.identities?.map(identity => (
                      <div key={identity.id} className="p-4">
                        <div className="font-medium">{identity.user?.name || 'Unknown'}</div>
                        <div className="text-sm text-neutral-400">{identity.user?.email}</div>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-500">Approve</Button>
                          <Button size="sm" variant="outline" className="border-red-500 text-red-400">Reject</Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="bg-neutral-900 rounded-xl border border-neutral-800">
                <div className="p-4 border-b border-neutral-800">
                  <h3 className="font-semibold">Certification Reviews</h3>
                  <span className="text-sm text-neutral-400">{verificationQueue.certifications_count || 0} pending</span>
                </div>
                <div className="divide-y divide-neutral-800">
                  {verificationQueue.certifications?.length === 0 ? (
                    <div className="p-8 text-center text-neutral-500">No pending certifications</div>
                  ) : (
                    verificationQueue.certifications?.map(cert => (
                      <div key={cert.id} className="p-4">
                        <div className="font-medium">{cert.certification_type?.name || 'Unknown'}</div>
                        <div className="text-sm text-neutral-400">{cert.user?.name}</div>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-500">Approve</Button>
                          <Button size="sm" variant="outline" className="border-red-500 text-red-400">Reject</Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== PAYMENTS TAB ========== */}
        {activeTab === 'payments' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Payments & Finance</h2>
            
            <div className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-800 text-left">
                    <th className="p-4 text-neutral-400 font-medium">Payment ID</th>
                    <th className="p-4 text-neutral-400 font-medium">Job</th>
                    <th className="p-4 text-neutral-400 font-medium">Amount</th>
                    <th className="p-4 text-neutral-400 font-medium">Platform Fee</th>
                    <th className="p-4 text-neutral-400 font-medium">Status</th>
                    <th className="p-4 text-neutral-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} className="border-b border-neutral-800 hover:bg-neutral-800/50">
                      <td className="p-4 font-mono text-sm">{p.id?.slice(0, 8)}...</td>
                      <td className="p-4 text-sm">{p.job_id?.slice(0, 8)}...</td>
                      <td className="p-4">£{p.amount?.toFixed(2)}</td>
                      <td className="p-4 text-green-400">£{(p.amount * 0.06).toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          p.status === 'captured' ? 'bg-green-500/20 text-green-400' :
                          p.status === 'refunded' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="p-4">
                        {p.status === 'captured' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const reason = prompt('Refund reason:');
                              if (reason) handleRefund(p.id, reason);
                            }}
                          >
                            <XCircle className="w-4 h-4 text-red-400" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========== AUDIT LOGS TAB ========== */}
        {activeTab === 'audit' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Audit Logs</h2>
            
            <div className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-800 text-left">
                    <th className="p-4 text-neutral-400 font-medium">Timestamp</th>
                    <th className="p-4 text-neutral-400 font-medium">Admin</th>
                    <th className="p-4 text-neutral-400 font-medium">Action</th>
                    <th className="p-4 text-neutral-400 font-medium">Target</th>
                    <th className="p-4 text-neutral-400 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className="border-b border-neutral-800 hover:bg-neutral-800/50">
                      <td className="p-4 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="p-4">
                        <div className="text-sm">{log.admin_email}</div>
                        <div className="text-xs text-neutral-500">{log.admin_role}</div>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                          {log.action}
                        </span>
                      </td>
                      <td className="p-4 text-sm">{log.target_type}: {log.target_id?.slice(0, 8)}...</td>
                      <td className="p-4 text-sm text-neutral-400">
                        {JSON.stringify(log.details || {}).slice(0, 50)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========== PRICING V2 TAB ========== */}
        {activeTab === 'pricing' && (
          <div className="p-6">
            <AdminPricingSettings token={getToken()} />
          </div>
        )}

        {/* ========== SETTINGS TAB ========== */}
        {activeTab === 'settings' && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Platform Settings</h2>
            
            <div className="grid gap-6 max-w-2xl">
              <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6">
                <h3 className="font-semibold mb-4">Pricing Configuration</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Customer Platform Fee</div>
                      <div className="text-sm text-neutral-400">Fee charged to customers</div>
                    </div>
                    <div className="text-2xl font-bold text-green-400">0%</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Contractor Platform Fee</div>
                      <div className="text-sm text-neutral-400">Fee deducted from contractor payouts</div>
                    </div>
                    <div className="text-2xl font-bold">6%</div>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6">
                <h3 className="font-semibold mb-4">Revenue Model</h3>
                <div className="p-4 bg-black rounded-lg">
                  <p className="text-neutral-400">
                    Uber-like model: <span className="text-white">Customers pay £0 platform fee.</span>
                  </p>
                  <p className="text-neutral-400 mt-2">
                    ZOLT takes <span className="text-green-400">6%</span> from contractor payouts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ========== ROLE CHANGE MODAL ========== */}
      {showRoleModal && roleChangeTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowRoleModal(false)}>
          <div className="bg-neutral-900 rounded-2xl p-6 max-w-md w-full mx-4 border border-neutral-700" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Change User Role</h3>
            
            <div className="mb-4">
              <div className="text-neutral-400 text-sm">User</div>
              <div className="font-medium">{roleChangeTarget.name}</div>
              <div className="text-sm text-neutral-500">{roleChangeTarget.email}</div>
            </div>
            
            <div className="mb-4">
              <div className="text-neutral-400 text-sm mb-1">Current Role</div>
              <span className={`px-2 py-1 rounded text-xs border ${getRoleBadgeColor(roleChangeTarget.role)}`}>
                {roleChangeTarget.role}
              </span>
            </div>
            
            <div className="mb-6">
              <label className="text-neutral-400 text-sm mb-2 block">New Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-3"
              >
                {ALL_ROLES.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-200">
                  This action will be logged in the audit trail. Role changes take effect immediately.
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={() => setShowRoleModal(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleRoleChange} 
                className="flex-1 bg-blue-600 hover:bg-blue-500"
                disabled={newRole === roleChangeTarget.role}
              >
                Confirm Change
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== JOB DETAIL MODAL ========== */}
      {selectedJob && jobDetail && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedJob(null); setJobDetail(null); }}>
          <div className="bg-neutral-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto border border-neutral-700" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-900">
              <div>
                <h3 className="text-xl font-bold">{jobDetail.job?.category} - {jobDetail.job?.subcategory}</h3>
                <div className="text-sm text-neutral-400">Job ID: {jobDetail.job?.id}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded text-sm ${getStatusColor(jobDetail.job?.status)} bg-opacity-20`}>
                  {jobDetail.job?.status?.replace('_', ' ')}
                </span>
                <Button variant="ghost" onClick={() => { setSelectedJob(null); setJobDetail(null); }}>
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 grid grid-cols-2 gap-6">
              {/* Customer Info */}
              <div className="bg-neutral-800 rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Customer
                </h4>
                <div className="space-y-2 text-sm">
                  <div><span className="text-neutral-400">Name:</span> {jobDetail.customer?.name}</div>
                  <div><span className="text-neutral-400">Email:</span> {jobDetail.customer?.email}</div>
                  <div><span className="text-neutral-400">Phone:</span> {jobDetail.customer?.phone || 'N/A'}</div>
                </div>
              </div>
              
              {/* Contractor Info */}
              <div className="bg-neutral-800 rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Contractor
                </h4>
                {jobDetail.contractor ? (
                  <div className="space-y-2 text-sm">
                    <div><span className="text-neutral-400">Name:</span> {jobDetail.contractor?.name}</div>
                    <div><span className="text-neutral-400">Company:</span> {jobDetail.contractor_profile?.company_name || 'N/A'}</div>
                    <div><span className="text-neutral-400">Rating:</span> ★ {jobDetail.contractor_profile?.rating?.toFixed(1) || '5.0'}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-400">Status:</span>
                      <span className={`w-2 h-2 rounded-full ${jobDetail.contractor_profile?.is_online ? 'bg-green-500' : 'bg-neutral-600'}`}></span>
                      {jobDetail.contractor_profile?.is_online ? 'Online' : 'Offline'}
                    </div>
                  </div>
                ) : (
                  <div className="text-neutral-500">No contractor assigned</div>
                )}
              </div>
              
              {/* Address */}
              <div className="bg-neutral-800 rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Address
                </h4>
                <div className="space-y-2 text-sm">
                  <div>{jobDetail.address?.formatted_address || jobDetail.job?.address_text}</div>
                  <div className="text-neutral-400">
                    Postcode: {jobDetail.address?.postal_code || 'N/A'}
                  </div>
                  {jobDetail.address?.lat && (
                    <div className="text-xs text-neutral-500">
                      Coords: {jobDetail.address.lat.toFixed(4)}, {jobDetail.address.lng.toFixed(4)}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Pricing Breakdown */}
              <div className="bg-neutral-800 rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Pricing
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Diagnostic Fee:</span>
                    <span>£{(jobDetail.pricing_breakdown?.diagnostic_fee_amount || jobDetail.pricing_breakdown?.callout_fee || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Labour (est.):</span>
                    <span>£{(jobDetail.pricing_breakdown?.estimate_labour_low || jobDetail.pricing_breakdown?.labor_estimate || 0).toFixed(0)}–{(jobDetail.pricing_breakdown?.estimate_labour_high || jobDetail.pricing_breakdown?.labor_estimate || 0).toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Materials (est.):</span>
                    <span>£{(jobDetail.pricing_breakdown?.estimate_materials_low || 0).toFixed(0)}–{(jobDetail.pricing_breakdown?.estimate_materials_high || jobDetail.pricing_breakdown?.materials_estimate || 0).toFixed(0)}</span>
                  </div>
                  <hr className="border-neutral-700" />
                  <div className="flex justify-between font-semibold">
                    <span>Customer Total:</span>
                    <span>£{jobDetail.pricing_breakdown?.customer_total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-red-400">
                    <span>Platform Fee (6%):</span>
                    <span>-£{jobDetail.pricing_breakdown?.platform_fee_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-400 font-semibold">
                    <span>Contractor Payout:</span>
                    <span>£{jobDetail.pricing_breakdown?.contractor_net_payout?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Payment Status */}
              <div className="bg-neutral-800 rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Payment
                </h4>
                {jobDetail.payment ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Status:</span>
                      <span className={jobDetail.payment.status === 'captured' ? 'text-green-400' : 'text-yellow-400'}>
                        {jobDetail.payment.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Amount:</span>
                      <span>£{jobDetail.payment.amount?.toFixed(2)}</span>
                    </div>
                    {jobDetail.payment.stripe_payment_intent_id && (
                      <div className="text-xs text-neutral-500 mt-2">
                        Stripe: {jobDetail.payment.stripe_payment_intent_id.slice(0, 20)}...
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-neutral-500">No payment recorded</div>
                )}
              </div>
              
              {/* Offer History */}
              <div className="bg-neutral-800 rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Offer History ({jobDetail.offer_count || 0})
                </h4>
                <div className="space-y-2 max-h-40 overflow-auto">
                  {jobDetail.offers?.map((offer, i) => (
                    <div key={i} className="text-sm p-2 bg-neutral-900 rounded">
                      <div className="flex justify-between">
                        <span>{offer.contractor?.name || 'Unknown'}</span>
                        <span className={`text-xs ${
                          offer.status === 'accepted' ? 'text-green-400' :
                          offer.status === 'declined' ? 'text-red-400' :
                          'text-yellow-400'
                        }`}>{offer.status}</span>
                      </div>
                      <div className="text-xs text-neutral-500">
                        {new Date(offer.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Timeline */}
            <div className="px-6 pb-4">
              <h4 className="font-semibold mb-3">Status Timeline</h4>
              <div className="flex gap-2 flex-wrap">
                {jobDetail.timeline?.map((event, i) => (
                  <div key={i} className={`px-3 py-1 rounded-full text-xs ${getStatusColor(event.status)} bg-opacity-20`}>
                    {event.status} • {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : 'N/A'}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Admin Actions */}
            <div className="p-6 border-t border-neutral-800 flex gap-3 flex-wrap">
              <Button
                onClick={() => {
                  const reason = prompt('Cancellation reason:');
                  if (reason) {
                    const refund = window.confirm('Process refund?');
                    handleCancelJob(jobDetail.job.id, reason, refund);
                  }
                }}
                variant="outline"
                className="border-red-500 text-red-400 hover:bg-red-500/20"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel Job
              </Button>
              
              <Button
                onClick={() => {
                  const reason = prompt('Re-dispatch reason:');
                  if (reason !== null) handleRedispatch(jobDetail.job.id, reason);
                }}
                variant="outline"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Re-Dispatch
              </Button>
              
              <Button
                onClick={() => searchContractorsForAssignment(jobDetail.job.id)}
                variant="outline"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Assign Contractor
              </Button>
              
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Amend Job
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== ASSIGN CONTRACTOR MODAL ========== */}
      {showAssignContractorModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => { setShowAssignContractorModal(false); setAssignmentWarnings(null); }}>
          <div className="bg-neutral-900 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto border border-neutral-700" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-neutral-800 sticky top-0 bg-neutral-900">
              <h3 className="text-xl font-bold">Assign Contractor</h3>
              <p className="text-sm text-neutral-400">Select a contractor to manually assign to this job</p>
            </div>
            
            {/* Assignment Warnings */}
            {assignmentWarnings && (
              <div className="p-4 bg-yellow-500/10 border-b border-yellow-500/30">
                <h4 className="font-semibold text-yellow-400 mb-2">⚠️ Warnings</h4>
                <ul className="text-sm text-yellow-200 space-y-1">
                  {assignmentWarnings.warnings?.map((w, i) => (
                    <li key={i}>• {w}</li>
                  ))}
                </ul>
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={() => handleForceAssign(selectedJob?.id || jobDetail?.job?.id, assignmentWarnings.contractor?.id, true)}
                    className="bg-yellow-600 hover:bg-yellow-500"
                  >
                    Override & Assign Anyway
                  </Button>
                  <Button variant="outline" onClick={() => setAssignmentWarnings(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            <div className="divide-y divide-neutral-800">
              {availableContractors.map(c => (
                <div key={c.contractor?.user_id} className="p-4 hover:bg-neutral-800/50 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.user?.name}</span>
                      {c.verified && <Shield className="w-4 h-4 text-blue-400" />}
                      <div className={`w-2 h-2 rounded-full ${c.is_online ? 'bg-green-500' : 'bg-neutral-600'}`} />
                    </div>
                    <div className="text-sm text-neutral-400">{c.contractor?.company_name}</div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {c.distance_miles !== null ? `${c.distance_miles} miles • ` : ''}
                      {c.jobs_completed} jobs • ★ {c.rating?.toFixed(1)}
                      {c.has_active_job && <span className="text-yellow-400 ml-2">(Has active job)</span>}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleForceAssign(selectedJob?.id || jobDetail?.job?.id, c.contractor?.user_id)}
                    size="sm"
                    disabled={!!assignmentWarnings}
                  >
                    Assign
                  </Button>
                </div>
              ))}
              {availableContractors.length === 0 && (
                <div className="p-8 text-center text-neutral-500">
                  No contractors found matching criteria
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-neutral-800">
              <Button onClick={() => { setShowAssignContractorModal(false); setAssignmentWarnings(null); }} variant="outline" className="w-full">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== USER DETAIL MODAL ========== */}
      {selectedUser && !showRoleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setSelectedUser(null)}>
          <div className="bg-neutral-900 rounded-2xl p-6 max-w-lg w-full mx-4 border border-neutral-700" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">User Details</h3>
            <div className="space-y-3">
              <div><span className="text-neutral-400">Name:</span> <span className="ml-2">{selectedUser.name}</span></div>
              <div><span className="text-neutral-400">Email:</span> <span className="ml-2">{selectedUser.email}</span></div>
              <div><span className="text-neutral-400">Phone:</span> <span className="ml-2">{selectedUser.phone || 'N/A'}</span></div>
              <div>
                <span className="text-neutral-400">Role:</span>
                <span className={`ml-2 px-2 py-1 rounded text-xs border ${getRoleBadgeColor(selectedUser.role)}`}>
                  {selectedUser.role}
                </span>
              </div>
              <div><span className="text-neutral-400">Created:</span> <span className="ml-2">{new Date(selectedUser.created_at).toLocaleDateString()}</span></div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={() => setSelectedUser(null)} variant="outline" className="flex-1">
                Close
              </Button>
              {isSuperAdmin && (
                <Button
                  onClick={() => {
                    setRoleChangeTarget(selectedUser);
                    setNewRole(selectedUser.role);
                    setShowRoleModal(true);
                  }}
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Change Role
                </Button>
              )}
              <Button
                onClick={() => {
                  const msg = prompt('Send message as ZOLT Support:');
                  if (msg) handleSendMessage(selectedUser.id, msg);
                }}
                className="flex-1"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
